import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { formatCurrency, formatPercent } from "@/utils/formatters";
import ColoredCurrency from "./ColoredCurrency";
import ColoredPercent from "./ColoredPercent";
import api from "@/api";

const HoldingsTable = ({ accountId, refreshTrigger = 0 }) => {
  const [sortField, setSortField] = useState("unrealized_gain_loss");
  const [sortDirection, setSortDirection] = useState("desc");
  const [holdings, setHoldings] = useState([]);
  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHoldings = async () => {
      setLoading(true);
      try {
        let response;
        if (accountId === null) {
          // Per-(account, ticker) rows with account attribution.
          response = await api.get("/accounts/all/holdings");
        } else {
          response = await api.get(`/accounts/${accountId}/current`);
        }

        setHoldings(response.data.holdings || []);
        setAccountData(response.data);
      } catch (error) {
        console.error("Error fetching holdings:", error);
        setHoldings([]);
        setAccountData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchHoldings();
  }, [accountId, refreshTrigger]);

  // Use backend-calculated values directly (no client-side math!)
  const cashBalance = accountData?.cash_balance || 0;
  const totalAccountValue = accountData?.total_value || 0;
  const totalStockValue = accountData?.portfolio_value || 0;
  const totalCostBasis = accountData?.cost_basis || 0;
  const totalUnrealizedPnL = accountData?.unrealized_gain_loss || 0;
  const totalUnrealizedPnLPercent =
    accountData?.unrealized_gain_loss_percent || 0;
  const stocksPercentOfAccount = accountData?.stocks_percent_of_account || 0;
  const cashPercentOfAccount = accountData?.cash_percent_of_account || 0;
  const accountTotal = totalAccountValue;

  // Aggregate today's gain/loss across stock holdings. Skip rows where today's
  // P&L is null (no price change data) rather than counting them as $0.
  const todaysGainDollarsTotal = holdings.reduce((sum, h) => {
    const v = h.todays_gain_loss;
    return v === null || v === undefined ? sum : sum + parseFloat(v);
  }, 0);
  // Yesterday's value = today's market value − today's P&L. Divide for portfolio %.
  const yesterdayStockValue =
    parseFloat(totalStockValue) - todaysGainDollarsTotal;
  const todaysGainPercentTotal =
    yesterdayStockValue > 0
      ? (todaysGainDollarsTotal / yesterdayStockValue) * 100
      : null;
  const hasAnyTodayData = holdings.some(
    (h) => h.todays_gain_loss !== null && h.todays_gain_loss !== undefined,
  );

  const sortedHoldings = [...holdings].sort((a, b) => {
    // Handle both string and numeric fields
    const aVal = a[sortField];
    const bVal = b[sortField];

    // For ticker/symbol and account_name, use string comparison
    if (sortField === "ticker" || sortField === "account_name") {
      const comparison = String(aVal ?? "").localeCompare(String(bVal ?? ""));
      return sortDirection === "asc" ? comparison : -comparison;
    }

    // For numeric fields, parse and compare
    const aNum = parseFloat(aVal) || 0;
    const bNum = parseFloat(bVal) || 0;

    if (sortDirection === "asc") {
      return aNum - bNum;
    }
    return bNum - aNum;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const getSortIndicator = (field) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (holdings.length === 0) {
    return (
      <div className="empty-state" data-testid="no-holdings">
        <h3>No holdings yet</h3>
        <p>Add your first stock position to start tracking</p>
      </div>
    );
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table className="performance-table data-table">
        <thead>
          <tr>
            <th
              onClick={() => handleSort("ticker")}
              style={{ textAlign: "left" }}
            >
              Symbol {getSortIndicator("ticker")}
            </th>
            {accountId === null && (
              <th
                onClick={() => handleSort("account_name")}
                style={{ textAlign: "left" }}
              >
                Account {getSortIndicator("account_name")}
              </th>
            )}
            <th
              onClick={() => handleSort("current_price")}
              style={{ textAlign: "right" }}
            >
              Last Price {getSortIndicator("current_price")}
            </th>
            <th
              onClick={() => handleSort("price_change")}
              style={{ textAlign: "right" }}
            >
              Last Price Change {getSortIndicator("price_change")}
            </th>
            <th
              onClick={() => handleSort("todays_gain_loss")}
              style={{ textAlign: "right" }}
            >
              Today's Gain/Loss $ {getSortIndicator("todays_gain_loss")}
            </th>
            <th
              onClick={() => handleSort("todays_gain_loss_percent")}
              style={{ textAlign: "right" }}
            >
              Today's Gain/Loss % {getSortIndicator("todays_gain_loss_percent")}
            </th>
            <th
              onClick={() => handleSort("unrealized_gain_loss")}
              style={{ textAlign: "right" }}
            >
              Total Gain/Loss $ {getSortIndicator("unrealized_gain_loss")}
            </th>
            <th
              onClick={() => handleSort("unrealized_gain_loss_percent")}
              style={{ textAlign: "right" }}
            >
              Total Gain/Loss %{" "}
              {getSortIndicator("unrealized_gain_loss_percent")}
            </th>
            <th
              onClick={() => handleSort("cagr")}
              style={{ textAlign: "right" }}
              title="Compound Annual Growth Rate - annualized return since first purchase"
            >
              CAGR % {getSortIndicator("cagr")}
            </th>
            <th
              onClick={() => handleSort("market_value")}
              style={{ textAlign: "right" }}
            >
              Current Value {getSortIndicator("market_value")}
            </th>
            <th
              onClick={() => handleSort("percent_of_account")}
              style={{ textAlign: "right" }}
            >
              % of Account {getSortIndicator("percent_of_account")}
            </th>
            <th
              onClick={() => handleSort("shares")}
              style={{ textAlign: "right" }}
            >
              Quantity {getSortIndicator("shares")}
            </th>
            <th
              onClick={() => handleSort("avg_cost_basis")}
              style={{ textAlign: "right" }}
            >
              Avg Cost Basis {getSortIndicator("avg_cost_basis")}
            </th>
            <th
              onClick={() => handleSort("cost_basis_total")}
              style={{ textAlign: "right" }}
            >
              Cost Basis Total {getSortIndicator("cost_basis_total")}
            </th>
            <th
              onClick={() => handleSort("week_52_high")}
              style={{ textAlign: "right" }}
            >
              52-Week Range {getSortIndicator("week_52_high")}
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Stocks Header */}
          <tr
            style={{
              borderBottom: "2px solid var(--border-color)",
              fontWeight: "700",
              background: "var(--bg-emphasis)",
            }}
          >
            <td style={{ padding: "0.75rem 1rem", textAlign: "left" }}>
              Stocks
            </td>
            {/* Account (aggregate view only) + Last Price + Last Price Change */}
            <td colSpan={accountId === null ? "3" : "2"}></td>
            <td style={{ padding: "0.75rem", textAlign: "right" }}>
              {hasAnyTodayData ? (
                <ColoredCurrency value={todaysGainDollarsTotal} />
              ) : (
                "—"
              )}
            </td>
            <td style={{ padding: "0.75rem", textAlign: "right" }}>
              {hasAnyTodayData && todaysGainPercentTotal !== null ? (
                <ColoredPercent value={todaysGainPercentTotal} />
              ) : (
                "—"
              )}
            </td>
            <td style={{ padding: "0.75rem", textAlign: "right" }}>
              <ColoredCurrency value={totalUnrealizedPnL} />
            </td>
            <td style={{ padding: "0.75rem", textAlign: "right" }}>
              <ColoredPercent value={totalUnrealizedPnLPercent} />
            </td>
            <td>{/* TODO: is there an account-wide CAGR to show here?! */}</td>
            <td style={{ padding: "0.75rem", textAlign: "right" }}>
              {formatCurrency(totalStockValue)}
            </td>
            <td
              style={{
                padding: "0.75rem",
                fontSize: "0.875rem",
                textAlign: "right",
              }}
            >
              {/* TODO: this is 0% */}
              {formatPercent(stocksPercentOfAccount)}
            </td>
            <td colSpan="4"></td>
          </tr>

          {/* Stock Holdings */}
          {sortedHoldings.map((holding) => {
            // Use backend-calculated values directly
            const costBasisTotal = holding.cost_basis_total;
            const percentOfAccount = holding.percent_of_account;
            const pnlPercent = holding.unrealized_gain_loss_percent;

            return (
              <tr key={holding.id} style={{ background: "var(--bg-row)" }}>
                <td style={{ paddingLeft: "2rem" }}>
                  <Link
                    to={`/ticker/${holding.ticker}${accountId && accountId !== "all" ? `?accountId=${accountId}` : holding.account_id && holding.account_id !== "all" ? `?accountId=${holding.account_id}` : ''}`}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <strong
                      data-testid={`ticker-${holding.ticker}`}
                      style={{ cursor: "pointer" }}
                    >
                      {holding.ticker}
                    </strong>
                  </Link>
                </td>
                {accountId === null && (
                  <td style={{ textAlign: "left", fontSize: "0.875rem", color: "var(--text-muted)" }}>
                    {holding.account_name || "—"}
                  </td>
                )}
                <td
                  data-testid={`current-price-${holding.ticker}`}
                  style={{ textAlign: "right" }}
                >
                  {formatCurrency(holding.current_price)}
                </td>
                <td style={{ fontSize: "0.875rem", textAlign: "right" }}>
                  {holding.price_change !== null &&
                  holding.price_change !== undefined ? (
                    <ColoredCurrency value={holding.price_change} />
                  ) : (
                    <span style={{ color: "var(--text-muted)" }}>—</span>
                  )}
                </td>
                <td style={{ textAlign: "right" }}>
                  {holding.todays_gain_loss !== null &&
                  holding.todays_gain_loss !== undefined ? (
                    <ColoredCurrency value={holding.todays_gain_loss} />
                  ) : (
                    <span
                      style={{
                        color: "var(--text-muted)",
                        fontSize: "0.875rem",
                      }}
                    >
                      —
                    </span>
                  )}
                </td>
                <td style={{ textAlign: "right" }}>
                  {holding.todays_gain_loss_percent !== null &&
                  holding.todays_gain_loss_percent !== undefined ? (
                    <ColoredPercent value={holding.todays_gain_loss_percent} />
                  ) : (
                    <span
                      style={{
                        color: "var(--text-muted)",
                        fontSize: "0.875rem",
                      }}
                    >
                      —
                    </span>
                  )}
                </td>
                <td
                  data-testid={`pnl-total-${holding.ticker}`}
                  style={{ textAlign: "right" }}
                >
                  <ColoredCurrency value={holding.unrealized_gain_loss} />
                </td>
                <td
                  data-testid={`pnl-percent-${holding.ticker}`}
                  style={{ textAlign: "right" }}
                >
                  <ColoredPercent value={pnlPercent} />
                </td>
                <td
                  data-testid={`cagr-${holding.ticker}`}
                  style={{ textAlign: "right", whiteSpace: "nowrap" }}
                >
                  {holding.cagr !== null && holding.cagr !== undefined ? (
                    <span
                      style={{
                        color:
                          parseFloat(holding.cagr) < 0
                            ? "var(--color-negative)"
                            : parseFloat(holding.cagr) < 5
                            ? "var(--color-warning)"
                            : parseFloat(holding.cagr) < 10
                            ? "var(--color-positive)"
                            : "var(--color-star)",
                        fontWeight:
                          parseFloat(holding.cagr) >= 10 ? "600" : "normal",
                      }}
                      title={`Held for ${
                        holding.holding_period_years
                          ? parseFloat(holding.holding_period_years).toFixed(1)
                          : "N/A"
                      } years`}
                    >
                      {parseFloat(holding.cagr) >= 10 && "🌟 "}
                      {formatPercent(holding.cagr)}
                    </span>
                  ) : (
                    <span
                      style={{
                        color: "var(--text-muted)",
                        fontSize: "0.875rem",
                      }}
                    >
                      —
                    </span>
                  )}
                </td>
                <td
                  data-testid={`current-value-${holding.ticker}`}
                  style={{ textAlign: "right" }}
                >
                  {formatCurrency(holding.market_value)}
                </td>
                <td style={{ fontSize: "0.875rem", textAlign: "right" }}>
                  {formatPercent(percentOfAccount)}
                </td>
                <td
                  data-testid={`quantity-${holding.ticker}`}
                  style={{ textAlign: "right" }}
                >
                  {holding.shares}
                </td>
                <td
                  data-testid={`entry-price-${holding.ticker}`}
                  style={{ textAlign: "right" }}
                >
                  {formatCurrency(holding.avg_cost_basis)}
                </td>
                <td style={{ fontSize: "0.875rem", textAlign: "right" }}>
                  {formatCurrency(costBasisTotal)}
                </td>
                <td
                  style={{
                    fontSize: "0.875rem",
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  }}
                >
                  {holding.week_52_low && holding.week_52_high ? (
                    `${formatCurrency(holding.week_52_low)} - ${formatCurrency(
                      holding.week_52_high
                    )}`
                  ) : (
                    <span style={{ color: "var(--text-muted)" }}>—</span>
                  )}
                </td>
              </tr>
            );
          })}

          {/* Stocks Footer Summary */}
          <tr
            style={{
              borderTop: "2px solid var(--border-color)",
              fontWeight: "700",
              background: "var(--bg-emphasis)",
            }}
          >
            <td
              colSpan={accountId === null ? "3" : "2"}
              style={{ padding: "0.75rem 1rem", textAlign: "left" }}
            >
              Stocks Subtotal
            </td>
            {/* Last Price + Last Price Change */}
            <td colSpan="1"></td>
            <td style={{ padding: "0.75rem", textAlign: "right" }}>
              {hasAnyTodayData ? (
                <ColoredCurrency value={todaysGainDollarsTotal} />
              ) : (
                "—"
              )}
            </td>
            <td style={{ padding: "0.75rem", textAlign: "right" }}>
              {hasAnyTodayData && todaysGainPercentTotal !== null ? (
                <ColoredPercent value={todaysGainPercentTotal} />
              ) : (
                "—"
              )}
            </td>
            <td style={{ padding: "0.75rem", textAlign: "right" }}>
              <ColoredCurrency value={totalUnrealizedPnL} />
            </td>
            <td style={{ padding: "0.75rem", textAlign: "right" }}>
              <ColoredPercent value={totalUnrealizedPnLPercent} />
            </td>
            <td>{/* TODO: is there an account-wide CAGR to show here?! */}</td>
            <td style={{ padding: "0.75rem", textAlign: "right" }}>
              {formatCurrency(totalStockValue)}
            </td>
            <td
              style={{
                padding: "0.75rem",
                fontSize: "0.875rem",
                textAlign: "right",
              }}
            >
              {formatPercent(stocksPercentOfAccount)}
            </td>
            <td colSpan="5"></td>
          </tr>

          {/* Cash Holdings Section */}
          {cashBalance !== undefined && (
            <>
              <tr
                style={{
                  borderTop: "2px solid var(--border-color)",
                  borderBottom: "2px solid var(--border-color)",
                  fontWeight: "700",
                  background: "var(--bg-emphasis)",
                }}
              >
                <td
                  colSpan={accountId === null ? "4" : "3"}
                  style={{ padding: "0.75rem 1rem", textAlign: "left" }}
                >
                  Cash & Cash Equivalents
                </td>
                <td colSpan="5"></td>
                <td style={{ padding: "0.75rem", textAlign: "right" }}>
                  {formatCurrency(cashBalance)}
                </td>
                <td
                  style={{
                    padding: "0.75rem",
                    fontSize: "0.875rem",
                    textAlign: "right",
                  }}
                >
                  {formatPercent(cashPercentOfAccount)}
                </td>
                <td colSpan="4"></td>
              </tr>
              <tr style={{ background: "var(--bg-row)" }}>
                <td style={{ paddingLeft: "2rem", textAlign: "left" }}>
                  <strong>Cash</strong>
                </td>
                <td colSpan={accountId === null ? "8" : "7"}></td>
                <td style={{ textAlign: "right" }}>
                  {formatCurrency(cashBalance)}
                </td>
                <td style={{ fontSize: "0.875rem", textAlign: "right" }}>
                  {formatPercent(cashPercentOfAccount)}
                </td>
                <td colSpan="4"></td>
              </tr>

              {/* Cash Footer Summary */}
              <tr
                style={{
                  borderTop: "2px solid var(--border-color)",
                  fontWeight: "700",
                  background: "var(--bg-emphasis)",
                }}
              >
                <td
                  colSpan={accountId === null ? "3" : "2"}
                  style={{ padding: "0.75rem 1rem", textAlign: "left" }}
                >
                  Cash Subtotal
                </td>
                <td colSpan="6"></td>
                <td style={{ padding: "0.75rem", textAlign: "right" }}>
                  {formatCurrency(cashBalance)}
                </td>
                <td
                  style={{
                    padding: "0.75rem",
                    fontSize: "0.875rem",
                    textAlign: "right",
                  }}
                >
                  {formatPercent(cashPercentOfAccount)}
                </td>
                <td colSpan="4"></td>
              </tr>
            </>
          )}

          {/* Account Totals */}
          <tr
            style={{
              borderTop: "3px solid var(--border-color)",
              fontWeight: "700",
              fontSize: "1rem",
              background: "var(--bg-subtle)",
            }}
          >
            <td colSpan={accountId === null ? "6" : "5"} style={{ padding: "1rem", textAlign: "left" }}>
              <strong>Account Total</strong>
            </td>
            <td style={{ padding: "1rem", textAlign: "right" }}>
              <ColoredCurrency value={totalUnrealizedPnL} />
            </td>
            <td style={{ padding: "1rem", textAlign: "right" }}>
              <ColoredPercent value={totalUnrealizedPnLPercent} />
            </td>
            <td></td>
            <td style={{ padding: "1rem", textAlign: "right" }}>
              <strong>{formatCurrency(accountTotal)}</strong>
            </td>
            <td style={{ padding: "1rem", textAlign: "right" }}>100%</td>
            <td colSpan="2"></td>
            <td style={{ padding: "1rem", textAlign: "right" }}>
              <strong>{formatCurrency(totalCostBasis)}</strong>
            </td>
            <td colSpan="2"></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default HoldingsTable;
